import React, { useMemo, useCallback } from "react";
import qs from "query-string";
import dayjs from "dayjs";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import { useTranslation } from "react-i18next";
import useToggle from "react-use/lib/useToggle";

import { toast } from "react-toastify";
import { useMutation, useQuery } from "@apollo/client";

import {
  getClientFeatureQuery,
  QUERY_CLIENT_TEAMS_PREFERENCES,
  QUERY_MS_TEAMS,
} from "../../../../config/graphql/query";
import {
  MUTATION_DELETE_MS_TEAMS_INTEGRATION,
  MUTATION_SET_CLIENT_TEAMS_MODE,
  TOGGLE_CLIENT_FEATURE,
} from "../../../../config/graphql/mutation";

import { useClientId } from "../hooks/useClient";

import { getApiUrl } from "../../../../lib/common/getApiUrl";
import { ToggleFeatureButton } from "../Shelly/ShellyControllers";
import useHasRole from "../../../../lib/hooks/useHasRole";

const TeamsIntegration = React.memo(() => {
  const [show, setShow] = useToggle(false);
  const { t } = useTranslation(["client", "common"]);

  const clientId = useClientId();

  const [onDelete] = useMutation(MUTATION_DELETE_MS_TEAMS_INTEGRATION, {
    refetchQueries: [
      {
        query: QUERY_MS_TEAMS,
      },
    ],
  });

  const { data: teamsData, loading } = useQuery<{ teams: ITeams }>(
    QUERY_MS_TEAMS,
    {
      context: {
        headers: {
          ClientId: clientId,
        },
      },
      fetchPolicy: "network-only",
    },
  );

  const hasTeamsIntegration = useMemo(
    () => !loading && !!teamsData?.teams,
    [teamsData, loading],
  );

  const canChangeTeamsMode = useHasRole(["ADMIN", "CLIENT_ADMIN"]);

  const { data: preferencesData } = useQuery<{
    client: { preferences?: { teams?: ITeamsPreferences } };
  }>(QUERY_CLIENT_TEAMS_PREFERENCES, {
    variables: { id: clientId },
    context: { headers: { ClientId: clientId } },
    fetchPolicy: "network-only",
  });

  const teamsMode = preferencesData?.client?.preferences?.teams?.mode ?? "own";

  const [setTeamsMode, { loading: changingMode }] = useMutation(
    MUTATION_SET_CLIENT_TEAMS_MODE,
    {
      refetchQueries: [
        {
          query: QUERY_CLIENT_TEAMS_PREFERENCES,
          variables: { id: clientId },
          context: { headers: { ClientId: clientId } },
        },
      ],
    },
  );

  const onChangeMode = useCallback(
    (mode: TeamsMode) =>
      setTeamsMode({ variables: { input: { id: clientId, mode } } })
        .then(() => {
          const switched =
            mode === "own" && !hasTeamsIntegration ? "stopped" : mode;

          toast.success<string>(
            t(`client:client.integrations.teams.shared.switched.${switched}`),
          );
        })
        .catch((error) =>
          toast.error<string>(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
          ),
        ),
    [clientId, hasTeamsIntegration, setTeamsMode, t],
  );

  const teamsId = useMemo(
    () => !loading && teamsData?.teams?.id,
    [teamsData, loading],
  );

  const onRemoveClientIntegration = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      return onDelete({ variables: { id: teamsId } })
        .then(() => {
          toast.success<string>(
            t("client:client.integrationForm.toast.deleted"),
          );
        })
        .catch((error) => {
          toast.error<string>(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
          );
        });
    },
    [teamsId, onDelete, t],
  );

  const onBeforeRemove = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();

      setShow(true);
    },
    [setShow],
  );

  const apiUrl = getApiUrl();

  if (teamsMode === "shared") {
    return (
      <div className="container-fluid">
        <Card className="mb-3">
          <Card.Header>
            {t("client:client.integrations.teams.shared.title")}
          </Card.Header>
          <Card.Body>
            <p>{t("client:client.integrations.teams.shared.body")}</p>
            <ul className="mb-3">
              <li>{t("client:client.integrations.teams.shared.senderName")}</li>
              <li>
                {t("client:client.integrations.teams.shared.firstContact")}
              </li>
              <li>{t("client:client.integrations.teams.shared.noGroups")}</li>
            </ul>
            <div className="d-flex justify-content-end">
              {canChangeTeamsMode && !hasTeamsIntegration && (
                <button
                  onClick={() => onChangeMode("own")}
                  className="btn btn-outline-secondary mr-2"
                  disabled={changingMode}
                >
                  {t("client:client.integrations.teams.shared.stopUsing")}
                </button>
              )}
              {canChangeTeamsMode &&
                (hasTeamsIntegration ? (
                  <button
                    onClick={() => onChangeMode("own")}
                    className="btn btn-outline-primary"
                    disabled={changingMode}
                  >
                    {t("client:client.integrations.teams.shared.useOwn")}
                  </button>
                ) : (
                  <a
                    href={qs.stringifyUrl(
                      {
                        url: `${apiUrl}/api/v1/teams/auth/login`,
                        query: {
                          client: clientId,
                          redirectUri: window.location.href,
                        },
                      },
                      { encode: true },
                    )}
                    className="btn btn-outline-primary"
                  >
                    {t("client:client.integrations.teams.shared.connectOwn")}
                  </a>
                ))}
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (!hasTeamsIntegration) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-end align-items-center my-4">
          {canChangeTeamsMode && (
            <button
              onClick={() => onChangeMode("shared")}
              className="btn btn-outline-secondary mr-2"
              disabled={changingMode}
            >
              {t("client:client.integrations.teams.shared.useShared")}
            </button>
          )}
          <a
            href={qs.stringifyUrl(
              {
                url: `${apiUrl}/api/v1/teams/auth/login`,
                query: {
                  client: clientId,
                  redirectUri: window.location.href,
                },
              },
              { encode: true },
            )}
            className="btn btn-primary"
          >
            {t("client:client.integrations.teams.button.generate")}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-end mb-3">
        <button onClick={onBeforeRemove} className="btn btn-danger">
          {t("client:client.integrations.teams.button.delete")}
        </button>
      </div>

      <Card className="mb-3">
        <Card.Header>
          {t("client:client.integrationForm.form.information")}
        </Card.Header>
        <Card.Body>
          {t("client:client.integrationForm.form.account")}
          {teamsData?.teams?.homeAccountId ?? ""}
          <hr />
          {t("client:client.integrationForm.form.email")}
          {teamsData?.teams?.email ?? ""}
          <hr />
          {t("client:client.integrationForm.form.name")}
          {teamsData?.teams?.name ?? ""}
          <hr />
          {t("client:client.integrationForm.form.created")}
          {dayjs(teamsData?.teams?.createdAt).format("L LT") ?? ""}
        </Card.Body>
      </Card>

      <Modal show={show} onHide={setShow} backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>
            {t("client:client.integrations.teams.modal.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("client:client.integrations.teams.modal.body")}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={setShow}>
            {t("common:cancel")}
          </Button>
          <Button variant="danger" onClick={onRemoveClientIntegration}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

const TeamsFeature: React.FC = () => {
  const { t } = useTranslation(["common"]);
  const clientId = useClientId();
  const isAdmin = useHasRole(["ADMIN"]);

  const { data } = useQuery(getClientFeatureQuery("teams"), {
    variables: { id: clientId },
    context: { headers: { ClientId: clientId } },
    fetchPolicy: "network-only",
  });

  const [toggleFeature] = useMutation(TOGGLE_CLIENT_FEATURE, {
    refetchQueries: [
      { query: getClientFeatureQuery("teams"), variables: { id: clientId } },
    ],
  });

  const teamsEnabled = data?.client?.preferences?.teams?.enabled ?? false;

  const onToggleTeams = useCallback(
    async (enabled: boolean) => {
      try {
        await toggleFeature({
          variables: {
            input: {
              id: clientId,
              feature: "teams",
              enabled,
            },
          },
        });
        toast.success<string>(
          t(`common:premiumFeature.${enabled ? "enabled" : "disabled"}`, {
            feature: "MS Teams",
          }),
        );
      } catch (e) {
        toast.error<string>(
          t(`common:premiumFeature.${enabled ? "enabled" : "disabled"}Error`, {
            feature: "MS Teams",
          }),
        );
      }
    },
    [clientId, toggleFeature, t],
  );

  if (!teamsEnabled) {
    return (
      <div className="tab-pane fade show active" id="teams" role="tabpanel">
        {isAdmin ? (
          <ToggleFeatureButton
            featureName="MS Teams"
            onClick={() => onToggleTeams(true)}
          />
        ) : (
          <div className="text-muted ml-3">
            <h5 className="mb-3">{t("common:premiumFeature.title")}</h5>
            <p>{t("common:premiumFeature.text")}</p>
            <p>{t("common:premiumFeature.contact")}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="tab-pane fade show active" id="teams" role="tabpanel">
      <TeamsIntegration />
      {isAdmin && (
        <div className="mt-3 ml-3">
          <ToggleFeatureButton
            featureName="MS Teams"
            disable
            onClick={() => onToggleTeams(false)}
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(TeamsFeature);
